import { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories as allCategories } from '@/mocks/categories';
import { articles as allArticles } from '@/mocks/articles';
import { applySeo, getCategoryPageSeo, getBreadcrumbSchema } from '@/utils/seo';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = allCategories.find((c) => c.slug === slug);
  const filteredArticles = allArticles.filter((a) => a.category === slug);
  const schemaInjected = useRef(false);
  const [activeCat, setActiveCat] = useState(slug || '');

  useEffect(() => {
    setActiveCat(slug || '');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  useEffect(() => {
    if (category) {
      applySeo(getCategoryPageSeo(category.slug, category.name, category.description));
    }
    if (!schemaInjected.current && category) {
      schemaInjected.current = true;
      const schema = getBreadcrumbSchema([
        { name: '首页', url: 'https://trainology.github.io' },
        { name: category.name, url: `https://trainology.github.io/category/${category.slug}` },
      ]);
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [category]);

  if (!category) {
    return (
      <main>
        <Navbar />
        <div className="pt-24 pb-14 text-center px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <Navbar />
      <div className="pt-20 pb-14 md:pb-20 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: category.name },
            ]}
          />

          <div className="mt-6">
            <div className="flex items-center gap-4 mb-2">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-500 text-background-50 rounded-xl">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600 mt-1">
                  共 {filteredArticles.length} 篇文章
                </p>
              </div>
            </div>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mt-4">
              {category.description}
            </p>
          </div>

          <div className="mt-10">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground-700 mr-2">分类切换：</span>
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 text-sm rounded-full transition-all duration-200 cursor-pointer border ${
                    activeCat === cat.slug
                      ? 'bg-primary-500 text-background-50 border-primary-500'
                      : 'bg-background-50 text-foreground-600 border-background-200/70 hover:border-primary-300/60 hover:text-primary-600'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filteredArticles.map((article) => (
              <ArticleCard
                key={article.slug}
                slug={article.slug}
                title={article.title}
                excerpt={article.excerpt}
                categoryName={article.categoryName}
                author={article.author}
                date={article.date}
                readTime={article.readTime}
                imageUrl={article.imageUrl}
              />
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
}